import React, { useState } from "react";
import { Popover } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { TooltipButton } from "components/common/TooltipButton";
import { MenuItem } from "components/common/MenuItem";

interface PendingAccessControlsProps {
  email: string;
  canRevokeAccess: boolean;
  onResend?: () => Promise<void>;
  onRevoke?: () => Promise<void>;
  onMenuToggle?: (isOpen: boolean) => void;
}

export const PendingAccessControls: React.FC<PendingAccessControlsProps> = ({
  canRevokeAccess,
  onResend,
  onRevoke,
  onMenuToggle,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const isMenuOpen = Boolean(anchorEl);
  const isBusy = isResending || isRevoking;

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isBusy) return;
    setAnchorEl(event.currentTarget);
    onMenuToggle?.(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    onMenuToggle?.(false);
  };

  const handleResend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onResend || isResending) return;
    setIsResending(true);
    handleMenuClose();
    try {
      await onResend();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 2000);
    } finally {
      setIsResending(false);
    }
  };

  const handleRevoke = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRevoke || isRevoking) return;
    setIsRevoking(true);
    handleMenuClose();
    try {
      await onRevoke();
    } finally {
      setIsRevoking(false);
    }
  };

  const hasActions = !!onResend || (canRevokeAccess && !!onRevoke);

  if (!hasActions) {
    return (
      <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
        <span className="text-fg-muted-medium shrink-0">Invited</span>
        <div className="flex-none w-5 h-5" />
      </div>
    );
  }

  return (
    <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
      {resendSuccess && (
        <span className="flex items-center justify-center w-3 h-3 rounded-full border border-success mr-1">
          <CheckIcon className="text-success" style={{ fontSize: 6 }} />
        </span>
      )}
      <span className="text-fg-muted-regular">Invited</span>
      <div
        className={`flex-none transition-opacity duration-200 ${
          isMenuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        } ${isBusy ? "opacity-40 pointer-events-none" : ""}`}
      >
        <TooltipButton
          title="Actions"
          disableTooltip={isMenuOpen}
          variant="contextual"
          size="xs"
          onClick={handleMenuOpen}
          className={isMenuOpen ? "button-active-state" : ""}
        >
          <MoreVertIcon />
        </TooltipButton>
      </div>

      <Popover
        open={isMenuOpen}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            className: "menu-surface",
          },
        }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          {onResend && (
            <MenuItem
              Icon={SendOutlinedIcon}
              label="Resend Invitation"
              onClick={handleResend}
              disabled={isBusy}
            />
          )}
          {canRevokeAccess && onRevoke && (
            <MenuItem
              Icon={PersonRemoveOutlinedIcon}
              label="Cancel Invite"
              onClick={handleRevoke}
              disabled={isBusy}
            />
          )}
        </div>
      </Popover>
    </div>
  );
};
